import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface ToastItem {
  id: number;
  text: string;
  error: boolean;
}
interface ToastApi {
  ok: (text: string) => void;
  error: (e: unknown) => void;
}

const Ctx = createContext<ToastApi>({ ok: () => undefined, error: () => undefined });
let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((text: string, error: boolean) => {
    const id = ++seq;
    setItems((xs) => [...xs, { id, text, error }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), error ? 7000 : 3500);
  }, []);
  const api: ToastApi = {
    ok: (t) => push(t, false),
    error: (e) => push(e instanceof Error ? e.message : String(e), true),
  };
  return (
    <Ctx.Provider value={api}>
      {children}
      {list(items.filter((t) => !t.error || !openDialog()))}
      {(() => {
        const dialog = openDialog();
        const errors = items.filter((t) => t.error);
        return dialog && errors.length ? createPortal(list(errors), dialog) : null;
      })()}
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);

function list(items: ToastItem[]) {
  return (
    <div className="toasts" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={`toast ${t.error ? "error" : ""}`} role={t.error ? "alert" : "status"}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

/** While a modal dialog is open the rest of the page is inert and under its backdrop, so an error shown there
 * would be dimmed and not announced: errors go inside the open dialog (G3 M3). Success messages stay outside:
 * they usually come as a dialog closes. */
function openDialog(): Element | null {
  if (typeof document === "undefined") return null;
  const open = document.querySelectorAll("dialog[open]");
  return open.length ? open[open.length - 1]! : null; // the topmost (last opened)
}
